import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {QuarantineApi, ReleaseProcessor} from "components/src/api/admin/generated";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {useState} from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import {useNavigate} from "react-router-dom";

const quarantineApi = new QuarantineApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface QuarantineReleaseDialogProps {
    queueIDs : string[]
    onClose : OnClose
}

export const QuarantineReleaseDialog = ({queueIDs, onClose} : QuarantineReleaseDialogProps) => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The release processor for released email
     */
    const [releaseProcessor, setReleaseProcessor] = useState<ReleaseProcessor>(ReleaseProcessor.Default);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while releasing the email to show a spinner on the download button
     */
    const [releasing, setReleasing] = useState<boolean>(false);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    // called when the dialog is closed without clicking download
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const release = () =>
    {
        logger.debug("Release mail")

        setReleasing(true);

        quarantineApi.quarantineControllerReleaseQuarantinedMails(queueIDs, releaseProcessor)
            .then(() => {
                onClose(DialogCloseCause.Succes);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error downloading mail", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setReleasing(false);
            });
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("quarantine.release-selected-email")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>

                <Form.Group className="mb-3">
                    <Form.Label>{t("quarantine.release-processor")}</Form.Label>
                    <Form.Select size="sm"
                                 aria-label={t("quarantine.release-processor")}
                                 value={releaseProcessor}
                                 onChange={(e) => setReleaseProcessor(Utils.stringToEnum(ReleaseProcessor, e.target.value as ReleaseProcessor))}>
                         <option value="DEFAULT">{t("common.default")}</option>
                        <option value="ENCRYPT">{t("common.encrypt")}</option>
                        <option value="AS_IS">{t("quarantine.no-further-processing")}</option>
                    </Form.Select>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success" onClick={release}>
                    {releasing && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                     {releasing ? t("common.releasing") : t("common.release")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
